import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/config";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { useSelector } from 'react-redux';

import {
  Home,
  GraduationCap,
        // still here in case you need it elsewhere
  BookOpen,
       // still here in case you need it elsewhere
  MessageSquare,
      // still here in case you need it elsewhere
  Calendar,
         // still here in case you need it elsewhere
  CalendarDays,
  /** ➤ NEW icons for the staff menu **/
  Search,
  Settings,
  FileText,
  ClipboardList,
  UserCheck,
  FlaskConical,
  DollarSign,
} from "lucide-react";

/** ▸ Staff‑specific sidebar items */
const menuItems = [
  { title: "Dashboard",     path: "/faculty-dashboard",               icon: Home },
  { title: "Academics",     path: "/dashboard/academics",     icon: BookOpen },
  { title: "Duty Roster",   path: "/dashboard/dutyroster",    icon: ClipboardList },
  { title: "Attendance",    path: "/dashboard/attendance",    icon: UserCheck },
  { title: "My Classes",    path: "/dashboard/myclasses",     icon: GraduationCap },
  { title: "Labs",          path: "/dashboard/labs",          icon: FlaskConical },
  { title: "Library",       path: "/dashboard/library",        icon: BookOpen },
  { title: "Finance",       path: "/finance-dashboard",        icon: DollarSign },
  { title: "Research",      path: "/dashboard/research",       icon: Search },
  { title: "Grading",       path: "/dashboard/grading",      icon: FileText },
  { title: "Timetable",     path: "/dashboard/timetable",     icon: Calendar },
  { title: "Exam Timetable", path: "/dashboard/exam-timetable", icon: CalendarDays },
  { title: "School Calendar", path: "/dashboard/school-calendar", icon: CalendarDays },
  { title: "Communication", path: "/dashboard/communication", icon: MessageSquare },
  { title: "explore",       path: "/dashboard/explore",       icon: Search },
  { title: "Settings",      path: "/dashboard/settings",      icon: Settings },
];

const Teachersidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  
  const [isLibrarian, setIsLibrarian] = useState(() => localStorage.getItem("isLibrarian") === "true");
  const [isBursar, setIsBursar] = useState(() => localStorage.getItem("isBursar") === "true");

  const currentUser = useSelector((state: any) => state.users?.currentUser);
  const subscriptionStatus = currentUser?.subscriptionStatus;
  const subscriptionExpiry = currentUser?.subscriptionExpiry;
  const isExpiredByDate = subscriptionExpiry ? Date.now() > subscriptionExpiry : false;
  const isInactive = (subscriptionStatus === 'expired' || subscriptionStatus === 'paused' || !subscriptionStatus) || isExpiredByDate;
  // New: user is logged in but not yet enrolled in any school
  const hasNoSchool = !currentUser?.selectedAffiliation?.schoolID;

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        localStorage.setItem("isLibrarian", "false");
        localStorage.setItem("isBursar", "false");
        setIsLibrarian(false);
        setIsBursar(false);
        return;
      }

      try {
        const tokenResult = await getIdTokenResult(user);
        const schoolID = tokenResult.claims.schoolID as string | undefined;

        if (!schoolID) {
          setIsLibrarian(false);
          setIsBursar(false);
          return;
        }

        const teachersRef = collection(db, `schools/${schoolID}/teachers`);
        const q = query(teachersRef, where("email", "==", user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const teacherDoc = querySnapshot.docs[0];
          const teacherData = teacherDoc.data();
          
          const tType = teacherData.teacherType;
          const types = Array.isArray(tType) ? tType : [tType];
          
          const isLib = types.includes("librarian");
          const isBur = types.includes("bursar");

          localStorage.setItem("isLibrarian", String(isLib));
          localStorage.setItem("isBursar", String(isBur));

          setIsLibrarian(isLib);
          setIsBursar(isBur);
        } else {
          setIsLibrarian(false);
          setIsBursar(false);
        }
      } catch (error) {
        console.error("Error checking status:", error);
        setIsLibrarian(false);
        setIsBursar(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleNavigation = (path: string) => {
    setActivePath(path);
    navigate(path);
  };

  // Filter menu items based on status
  const filteredMenuItems = menuItems.filter(item => {
    if (item.title === "Library") return isLibrarian;
    if (item.title === "Finance") return isBursar;
    return true;
  });

  return (
    <Sidebar className="w-48 sm:w-64 sticky top-0 h-screen self-start overflow-y-auto z-20 bg-white dark:bg-[#020817] border-r border-gray-200 border-none">
      <SidebarContent className="p-2 sm:p-4 dark:bg-[#020817] border-r border-y-gray-950 border-none">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
            Teacher Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {filteredMenuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    isActive={activePath === item.path}
                    className="w-full justify-start cursor-pointer p-2 sm:p-3 rounded-lg transition-all duration-200 touch-manipulation text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900 data-[active=true]:text-blue-700 dark:data-[active=true]:text-blue-300"
                    onClick={() => {
                      // Unenrolled: only Settings is accessible
                      if (hasNoSchool && item.title !== "Settings") return;
                      if (isInactive && item.title !== "Settings") {
                        navigate('/subscription-expired');
                        return;
                      }
                      handleNavigation(item.path);
                    }}
                    style={hasNoSchool && item.title !== "Settings" ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                  >
                    <item.icon className="mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default Teachersidebar;